import { ReactNode } from "react";
import { PanelRightIcon, PanelRightCloseIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";

// Detail-page two-column layout (DESIGN.md "布局"): main content left, a
// properties/context rail right (sticky), split proportionally 2:1 on lg+,
// stacking on small screens. Every detail page uses these instead of
// hand-rolling widths.
export const DetailColumns = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-8 lg:flex-row">{children}</div>
);

export const DetailMain = ({ children }: { children: ReactNode }) => (
  <main className="min-w-0 flex-[2]">{children}</main>
);

// `open`: the rail folds behind a shadcn Collapsible (Radix state, no
// hand-rolled toggle). The page owns the state and renders
// <DetailAsideToggle> wherever it fits (e.g. the header action row);
// collapsed, the rail disappears and the main column takes the full width.
export const DetailAside = ({
  children,
  open,
}: {
  children: ReactNode;
  open?: boolean;
}) => {
  if (open === undefined) {
    return (
      <aside className="w-full lg:min-w-0 lg:flex-1">
        <div className="space-y-6 lg:sticky lg:top-4">{children}</div>
      </aside>
    );
  }
  return (
    <Collapsible open={open} asChild>
      <aside className="w-full lg:min-w-0 lg:flex-1 data-[state=closed]:hidden">
        <CollapsibleContent className="space-y-6 lg:sticky lg:top-4">{children}</CollapsibleContent>
      </aside>
    </Collapsible>
  );
};

export const DetailAsideToggle = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Button
    variant="ghost"
    size="icon-sm"
    className="shrink-0 text-muted-foreground"
    aria-label="Toggle details panel"
    aria-expanded={open}
    title="Toggle details panel"
    onClick={() => onOpenChange(!open)}
  >
    {open ? <PanelRightCloseIcon className="h-4 w-4" /> : <PanelRightIcon className="h-4 w-4" />}
  </Button>
);
